import { useEffect } from 'react';

import { type JoinedWorkspaceRelation } from '../../contracts';
import { getWorkspaceId, setWorkspaceId } from '../atoms/workspaceAtoms';
import { useQueryWorkspaces } from './useQueryWorkspaces';

/**
 * On app boot / post-login, hydrate the persisted workspace id from
 * `GET /api/v2/ml-scribe/workspaces` so the `Heidi-Workspace-Id` header
 * (already injected by `scribeHTTP.ts`) starts flowing on every request.
 *
 * Behaviour:
 *  - First boot (atom is `null`) → set to the default workspace — UNLESS the
 *    user belongs to 2+ workspaces and `deferInitialPickWhenMultiple` is set,
 *    in which case the atom stays `null` and the post-login workspace selector
 *    becomes the writer. The caller flips the option back to `false`
 *    once the selector's flag settles OFF, and this effect then picks the
 *    default exactly as it always has.
 *  - Persisted id no longer in the user's membership list → reset to the
 *    default workspace (e.g. user was removed from that workspace).
 *  - Persisted id still valid → leave untouched.
 *  - Empty list with no persisted id (signup race before
 *    personal-workspace provisioning) → leave the atom `null` and let the
 *    backend `release-ml-scribe-infer-default-workspace` flag fall back to
 *    inference. Backward-compatible by design.
 *  - Empty list with a persisted id (removed from the last remaining
 *    workspace) → clear the stale id and do a full-page load on `/scribe`,
 *    so requests stop carrying a header the backend will 403.
 *
 * Logout cleanup is handled by `clearLogoutStorage` — the storage key is
 * already listed in `LOGOUT_KEYS`.
 *
 * Tracked by MUL-1991.
 *
 * Extracted to @heidi/workspace/data — history:
 * `git log --follow src/features/workspace/hooks/useHydrateWorkspaceId.ts`.
 */
export type HydrateWorkspaceIdOptions = {
  /**
   * Leave the atom `null` on first boot when the user belongs to 2+ workspaces,
   * so a selector can take the pick instead of the alphabetical default. Also
   * the right value while the flag behind that selector has not settled yet: a
   * default write is irreversible for the session, so it must not race an
   * unresolved flag. Single-workspace users are unaffected.
   */
  deferInitialPickWhenMultiple?: boolean;
};

export function useHydrateWorkspaceId({
  deferInitialPickWhenMultiple = false,
}: HydrateWorkspaceIdOptions = {}): void {
  const { data } = useQueryWorkspaces();

  useEffect(() => {
    if (!data) return;
    const { workspaces } = data;
    const persistedId = getWorkspaceId();

    // First boot / signup race: hydrate if possible, never reload (it would loop).
    if (persistedId === null) {
      if (workspaces.length > 1 && deferInitialPickWhenMultiple) {
        return;
      }
      if (workspaces.length > 0) {
        setWorkspaceId(pickDefaultWorkspaceId(workspaces));
      }
      return;
    }

    if (workspaces.some((w) => w.workspace_id === persistedId)) return;

    // Removed from the active (or last) workspace: reset the id, then do a
    // full-page load on /scribe — same pattern as the manual switcher — so all
    // workspace-scoped state restarts under it.
    setWorkspaceId(workspaces.length > 0 ? pickDefaultWorkspaceId(workspaces) : null);
    window.location.replace('/scribe');
  }, [data, deferInitialPickWhenMultiple]);
}

/**
 * The one ordering every workspace list shares: alphabetical by name, explicit
 * `'en'` locale so it is identical across user browser locales, base
 * sensitivity so case does not split the list. `pickDefaultWorkspaceId` takes
 * the head of it and `WorkspaceSelector` renders all of it, which is what keeps
 * "the first row is the workspace the app would auto-pick" true by
 * construction rather than by two copies staying in sync.
 */
export function sortWorkspacesByName(
  workspaces: JoinedWorkspaceRelation[]
): JoinedWorkspaceRelation[] {
  return [...workspaces].sort((a, b) =>
    a.workspace_name.localeCompare(b.workspace_name, 'en', { sensitivity: 'base' })
  );
}

/**
 * The M6 BE schema has no `is_default` field, so the deterministic tie-breaker
 * is the alphabetically-first workspace name. Workspace switching is M8.
 */
export function pickDefaultWorkspaceId(workspaces: JoinedWorkspaceRelation[]): string {
  const first = sortWorkspacesByName(workspaces)[0];
  if (!first) {
    throw new Error('pickDefaultWorkspaceId requires a non-empty workspaces array');
  }
  return first.workspace_id;
}
